import { validateAndFormatUrl } from "../../../Utils/StayUpToDate/RenderComponent";
import type {
  CustomWebsitePage,
  ExtraSectionSpec,
  PageDef,
  PageTweaks,
  SectionSpec,
  Tweaks,
} from "./types";

export const CUSTOM_PAGE_PREFIX = "c_";

export const RESERVED_PAGE_SLUGS = new Set([
  "home",
  "shop",
  "blog",
  "about",
  "contact",
  // Alias of website Contact — keep free of custom pages / collections.
  "contact-me",
  "content",
  "search",
  "press",
  "newsletter",
  "link-in-bio",
  "linkinbio",
  "platform",
  "journal",
  "products",
  "page",
  "pages",
  "mediakit",
  "brandpartners",
]);

export function isCustomWebsitePageKey(pageKey: string): boolean {
  return pageKey.startsWith(CUSTOM_PAGE_PREFIX);
}

/** Home, Link in Bio, and creator-made pages share the full add catalog. */
export function isFreeformAddPage(pageKey: string): boolean {
  return (
    pageKey === "home" ||
    pageKey === "linkInBio" ||
    isCustomWebsitePageKey(pageKey)
  );
}

/**
 * Pages that can open the Add inspector.
 * Products / Content: Featured options only (see `catalogTypeAvailableOnPage`).
 * Blog / About / Contact: no Add.
 */
export function canOpenAddComponent(pageKey: string): boolean {
  if (
    pageKey === "about" ||
    pageKey === "contact" ||
    pageKey === "journal" ||
    pageKey === "journalPost" ||
    pageKey === "blog"
  ) {
    return false;
  }
  return (
    isFreeformAddPage(pageKey) ||
    pageKey === "shop" ||
    pageKey === "content"
  );
}

export function listCustomWebsitePages(
  tweaks: Tweaks | undefined | null,
): CustomWebsitePage[] {
  const raw = tweaks?.customPages;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: CustomWebsitePage[] = [];
  for (const p of raw) {
    if (
      !p ||
      typeof p !== "object" ||
      typeof p.id !== "string" ||
      !p.id.startsWith(CUSTOM_PAGE_PREFIX)
    ) {
      continue;
    }
    if (seen.has(p.id)) continue;
    seen.add(p.id);
    out.push(p);
  }
  return out;
}

export function findCustomWebsitePage(
  tweaks: Tweaks | undefined | null,
  pageKey: string,
): CustomWebsitePage | undefined {
  const list = listCustomWebsitePages(tweaks);
  const byId = list.find((p) => p.id === pageKey);
  if (byId) return byId;
  // After a slug rename the public URL is `c_${newSlug}` while storage id stays
  // `c_${originalSlug}` — still resolve so extras/content load.
  if (!isCustomWebsitePageKey(pageKey)) return undefined;
  const slug = pageKey.slice(CUSTOM_PAGE_PREFIX.length).toLowerCase();
  if (!slug) return undefined;
  return list.find(
    (p) => String(p.slug || "").trim().toLowerCase() === slug,
  );
}

/** Stable storage key for `tweaks.pages[id]` (id does not change when slug is edited). */
export function customWebsitePageStorageKey(
  tweaks: Tweaks | undefined | null,
  pageKey: string,
): string {
  return findCustomWebsitePage(tweaks, pageKey)?.id || pageKey;
}

/** Match a public `/{username}/{slug}` segment to a creator-made page. */
export function findCustomWebsitePageByPublicSlug(
  tweaks: Tweaks | undefined | null,
  slug: string,
): CustomWebsitePage | undefined {
  const normalized = String(slug || "").trim().toLowerCase();
  if (!normalized) return undefined;
  const id = `${CUSTOM_PAGE_PREFIX}${normalized}`;
  return listCustomWebsitePages(tweaks).find(
    (p) =>
      p.id === id || String(p.slug || "").trim().toLowerCase() === normalized,
  );
}

/**
 * Live page-route typing: keep a trailing `-` so users can enter `look-book`.
 * Spaces / other punctuation become `-`; leading hyphens are still stripped.
 */
export function sanitizePageSlugInput(raw: string): string {
  return String(raw || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+/g, "")
    .slice(0, 48);
}

/** Final page slug: same as input sanitize, plus no trailing hyphen. */
export function slugifyPageName(name: string): string {
  return sanitizePageSlugInput(name).replace(/-+$/g, "");
}

/**
 * Normalize stored `nav.*.href` / `nav.slug.*` values before Publish so a
 * trailing `-` left while typing cannot break public route alias matching.
 */
export function finalizePageSlugContentEdits(
  contentEdits: Record<string, string> | null | undefined,
): Record<string, string> {
  const next: Record<string, string> = { ...(contentEdits || {}) };
  for (const [key, raw] of Object.entries(next)) {
    if (
      !(
        (key.startsWith("nav.") && key.endsWith(".href")) ||
        key.startsWith("nav.slug.")
      )
    ) {
      continue;
    }
    const href = String(raw ?? "").trim();
    if (!href || href === "/") {
      next[key] = "/";
      continue;
    }
    const body = href.replace(/^\//, "").split("/")[0] || "";
    const finalized = slugifyPageName(body);
    next[key] = finalized ? `/${finalized}` : "/";
  }
  return next;
}

export function customPageIdFromSlug(slug: string): string {
  return `${CUSTOM_PAGE_PREFIX}${slug}`;
}

export function blankCustomPageDef(page: CustomWebsitePage): PageDef {
  return {
    label: page.label || "Page",
    sections: [
      {
        id: "header",
        type: "PageHeader",
        label: "Header",
        props: {
          heading: page.label || "Page",
          lead: "",
        },
      },
    ],
  };
}

export function extraSectionsOf(
  pageState: PageTweaks | undefined,
): ExtraSectionSpec[] {
  const extra = pageState?.extra;
  if (!Array.isArray(extra)) return [];
  return extra.filter(
    (s): s is ExtraSectionSpec =>
      Boolean(s) &&
      typeof s === "object" &&
      typeof s.id === "string" &&
      typeof s.type === "string",
  );
}

export function mergePageDefWithExtra(
  def: PageDef | undefined,
  extra: ExtraSectionSpec[],
): PageDef | undefined {
  if (!def) return def;
  if (!extra.length) return def;
  const seen = new Set(def.sections.map((s) => s.id));
  const added: SectionSpec[] = [];
  for (const s of extra) {
    if (seen.has(s.id)) continue;
    seen.add(s.id);
    added.push({
      id: s.id,
      type: s.type,
      label: s.catalogType
        ? extraSectionLabelForCatalog(s.catalogType)
        : s.label || s.type,
      ...(s.catalogType ? { catalogType: s.catalogType } : {}),
    });
  }
  if (!added.length) return def;
  return { ...def, sections: [...def.sections, ...added] };
}

export function schemaBlockTypeForCatalog(catalogType: string): string | null {
  const SCHEMA_TYPES = new Set([
    "image",
    "text",
    "quote",
    "spacer",
    "cta",
    "ribbon",
    "video",
    "discount",
    "testimonials",
    "followers",
    "asseenon",
    "map",
    "accordion",
    "sectionheader",
    "vibe",
    "embed",
    "columns",
    "tabs",
  ]);
  return SCHEMA_TYPES.has(catalogType) ? "SchemaBlock" : null;
}

type ImageCfgLike = {
  blockType?: unknown;
  catalogType?: unknown;
  items?: unknown;
  layout?: unknown;
  motion?: unknown;
  autoscroll?: unknown;
  columns?: unknown;
  tabs?: unknown;
};

export const IMAGE_GALLERY_MARQUEE_MIN_IMAGES = 2;

export const IMAGE_GALLERY_MARQUEE_SECOND_IMAGE_REQUIRED =
  "Upload at least two images for Marquee.";

/** Count gallery slots with a finished CDN upload. */
export function imageGalleryUploadedCount(
  cfg: ImageCfgLike | null | undefined,
): number {
  if (!cfg || typeof cfg !== "object") return 0;
  if (String(cfg.blockType || cfg.catalogType || "") !== "image") return 0;
  const items = Array.isArray(cfg.items) ? cfg.items : [];
  return items.filter((it) =>
    isUploadedImageUrl((it as { photo?: unknown } | null)?.photo),
  ).length;
}

/** Carousel Marquee — needs 2+ uploaded images to loop. */
export function imageGalleryUsesLoopingMotion(
  cfg: ImageCfgLike | null | undefined,
): boolean {
  if (!cfg || typeof cfg !== "object") return false;
  if (String(cfg.blockType || cfg.catalogType || "") !== "image") return false;
  if (String(cfg.layout || "single") !== "carousel") return false;
  return String(cfg.motion || "static") === "marquee";
}

export function imageGalleryMarqueeSecondImageMissing(
  cfg: ImageCfgLike | null | undefined,
): boolean {
  if (!imageGalleryUsesLoopingMotion(cfg)) return false;
  return (
    imageGalleryUploadedCount(cfg) < IMAGE_GALLERY_MARQUEE_MIN_IMAGES
  );
}

/** A finished upload — not an empty slot, blob preview, or data URL. */
export function isUploadedImageUrl(value: unknown): boolean {
  const url = String(value ?? "").trim();
  if (!url) return false;
  if (url.startsWith("blob:") || url.startsWith("data:")) return false;
  return true;
}

export type ImageUploadGate = "ok" | "missing" | "uploading";

/**
 * Image / Gallery (including one nested in columns or tabs) cannot be added
 * until at least one photo has finished uploading.
 */
export function imageCfgNeedsUpload(
  cfg: ImageCfgLike | null | undefined,
): ImageUploadGate {
  if (!cfg || typeof cfg !== "object") return "ok";
  const kind = String(cfg.blockType || cfg.catalogType || "");
  if (kind === "image") {
    const items = Array.isArray(cfg.items) ? cfg.items : [];
    let uploading = false;
    for (const it of items) {
      const photo = String(
        (it as { photo?: unknown } | null)?.photo ?? "",
      ).trim();
      if (!photo) continue;
      if (photo.startsWith("blob:") || photo.startsWith("data:")) {
        uploading = true;
        continue;
      }
      if (isUploadedImageUrl(photo)) return "ok";
    }
    return uploading ? "uploading" : "missing";
  }
  const walk = (children: unknown): ImageUploadGate => {
    if (!Array.isArray(children)) return "ok";
    for (const child of children) {
      const state = imageCfgNeedsUpload(child as ImageCfgLike);
      if (state !== "ok") return state;
    }
    return "ok";
  };
  if (Array.isArray(cfg.columns)) {
    for (const col of cfg.columns) {
      const state = walk((col as { children?: unknown })?.children);
      if (state !== "ok") return state;
    }
  }
  if (Array.isArray(cfg.tabs)) {
    for (const tab of cfg.tabs) {
      const state = walk((tab as { children?: unknown })?.children);
      if (state !== "ok") return state;
    }
  }
  return "ok";
}

export const ACCORDION_QUESTION_REQUIRED = "Add at least one question.";
export const ACCORDION_ANSWER_REQUIRED = "Add an answer for every question.";

/** Rich-text body seeded when adding a Text block — treated as placeholder, not content. */
export const SCHEMA_TEXT_DEFAULT_BODY =
  "Editable here, or directly on the canvas. Use the toolbar for <b>bold</b>, <i>italic</i>, and lists.";

/**
 * Factory demo copy for schema blocks. Drawer inputs show these as placeholders.
 * Add/Edit preview never paints them — an empty section shows a drafting hint
 * until the creator enters real copy.
 */
export const SCHEMA_DRAFT_HINT = {
  ribbon: "Add a message using the editor",
  text: "Add text using the editor",
  quote: "Add a quote using the editor",
  discount: "Add a discount code using the editor",
  testimonials: "Add a testimonial using the editor",
  followers: "Add follower counts using the editor",
  asseenon: "Add a logo using the editor",
  map: "Add a location using the editor",
  accordion: "Add a question using the editor",
  sectionheader: "Add a heading using the editor",
  header: "Add a header using the editor",
  columns: "Add components using the editor",
  tabs: "Add content using the editor",
  spacer: "Adjust this spacer using the editor",
} as const;
export const SCHEMA_DEMO = {
  ribbon: {
    items: [
      {
        message: "Free shipping on orders over $75",
        ctaLabel: "Shop now",
      },
      {
        message: "New fall drop is live",
        ctaLabel: "See it",
      },
    ],
  },
  accordion: {
    heading: "FAQ",
    items: [
      {
        q: "Do you offer sizing help?",
        a: "Yes — DM sizing questions any time.",
      },
      {
        q: "How do I book a collab?",
        a: 'Use the Contact page and select "Brand inquiry."',
      },
    ],
  },
  discount: {
    items: [
      {
        brand: "Albion Fit",
        code: "CAROLINE20",
        desc: "20% off everything",
      },
      {
        brand: "Nordstrom",
        code: "CB15",
        desc: "15% off fall styles",
      },
    ],
  },
  testimonials: {
    heading: "What people say",
    items: [
      {
        quote: '"This changed how I get dressed every morning."',
        name: "Jess R.",
      },
      {
        quote: '"So easy to shop the exact pieces."',
        name: "Amanda K.",
      },
    ],
  },
  followers: {
    // Instructional placeholders only — must not equal real platform/count
    // values creators type (e.g. "Instagram"), or the drawer clears on match
    // and publish treats the row as still empty.
    items: [
      { platform: "Platform name", count: "Follower count" },
      { platform: "Platform name", count: "Follower count" },
      { platform: "Platform name", count: "Follower count" },
    ],
  },
  asseenon: {
    heading: "As Seen On",
    items: [
      { name: "Publication name" },
      { name: "Publication name" },
      { name: "Publication name" },
    ],
  },
  map: {
    address: "City, State",
    label: "Location label",
  },
  sectionheader: {
    eyebrow: "FEATURED",
    heading: "Shop the Edit",
  },
  columns: {
    heading: "Section headline",
  },
  tabs: {
    labels: ["Bio", "Press", "New Tab"],
  },
} as const;

function normDemoText(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/** Empty or exact factory demo — treat as unfilled placeholder. */
export function isSchemaDemoText(value: unknown, demo: unknown): boolean {
  const v = String(value ?? "").trim();
  if (!v) return true;
  const d = String(demo ?? "").trim();
  if (!d) return false;
  return normDemoText(v) === normDemoText(d);
}

/** Empty or factory demo tab titles (Bio / Press / New Tab) — not real copy. */
export function isSchemaTabLabelPlaceholder(label: unknown): boolean {
  const v = String(label ?? "").trim();
  if (!v) return true;
  return SCHEMA_DEMO.tabs.labels.some((demo) => isSchemaDemoText(v, demo));
}

/**
 * Drawer controlled value. Always the stored string — demo copy is only the
 * Input `placeholder`. Blanking on exact demo match wiped intentional values
 * like platform "Instagram" when that string matched SCHEMA_DEMO.
 */
export function schemaDrawerText(value: unknown, _demo?: unknown): string {
  return String(value ?? "");
}

/**
 * Preview / canvas text.
 *
 * Empty + pristine slots may show factory demo copy (`_showDemo`).
 * Never blank a stored value that happens to equal the demo string — that wiped
 * intentional FAQ copy when finishing a demo question (e.g. adding "?").
 * Drawer inputs use placeholders separately; publish still prunes demo seeds.
 */
export function schemaPreviewText(
  value: unknown,
  demo?: string,
  showDemo?: boolean,
): string {
  const stored = String(value ?? "");
  if (stored.trim()) return stored;
  if (showDemo) return String(demo ?? "");
  return "";
}

/** True when no item has left the factory/empty seed state. */
export function schemaItemsArePristine(
  items: unknown[] | null | undefined,
  demos: ReadonlyArray<Record<string, string>>,
  fields: string[],
): boolean {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return true;
  return !list.some((raw, i) => {
    const it = (raw || {}) as Record<string, unknown>;
    const demo = (demos[i] || demos[0] || {}) as Record<string, string>;
    return fields.some((f) => !isSchemaDemoText(it[f], demo[f]));
  });
}

/** Drop empty / demo-only slots before add/update so they never publish. */
export function pruneSchemaSeedItems(
  cfg: object | null | undefined,
): object | null | undefined {
  if (!cfg || typeof cfg !== "object") return cfg;
  const rec = { ...(cfg as Record<string, unknown>) };
  const kind = String(rec.blockType || rec.catalogType || "");
  const items = Array.isArray(rec.items) ? [...rec.items] : null;

  const keep = (fields: string[], demos: ReadonlyArray<Record<string, string>>) =>
    (items || []).filter((raw, i) => {
      const it = (raw || {}) as Record<string, unknown>;
      const demo = (demos[i] || demos[0] || {}) as Record<string, string>;
      if (kind === "asseenon" && String(it.photo || "").trim()) return true;
      return fields.some((f) => !isSchemaDemoText(it[f], demo[f]));
    });

  if (items) {
    let nextItems = items;
    if (kind === "ribbon") {
      nextItems = keep(["message"], SCHEMA_DEMO.ribbon.items).map((raw) => {
        const it = { ...((raw || {}) as Record<string, unknown>) };
        const url = String(it.url ?? "").trim();
        if (!url) return it;
        const check = validateAndFormatUrl(url);
        if (check.isValid && check.formatted) it.url = check.formatted;
        return it;
      });
    } else if (kind === "accordion") {
      nextItems = keep(["q", "a"], SCHEMA_DEMO.accordion.items);
    } else if (kind === "discount") {
      nextItems = keep(
        ["brand", "code", "desc", "expires", "url"],
        SCHEMA_DEMO.discount.items,
      );
    } else if (kind === "testimonials") {
      nextItems = keep(["quote", "name"], SCHEMA_DEMO.testimonials.items);
    } else if (kind === "followers") {
      nextItems = keep(["platform", "count"], SCHEMA_DEMO.followers.items);
    } else if (kind === "asseenon") {
      nextItems = keep(["name"], SCHEMA_DEMO.asseenon.items);
    }
    if (kind === "ribbon" ||
      kind === "accordion" ||
      kind === "discount" ||
      kind === "testimonials" ||
      kind === "followers" ||
      kind === "asseenon") {
      rec.items = nextItems;
    }
  }

  // Text: never persist the factory body hint.
  if (kind === "text") {
    if (isSchemaTextBodyPlaceholder(rec.body || rec.rich)) {
      rec.body = "";
      if ("rich" in rec) rec.rich = "";
    }
  }


  if (Array.isArray(rec.columns)) {
    rec.columns = (rec.columns as { children?: unknown[] }[]).map((col) => ({
      ...col,
      children: Array.isArray(col.children)
        ? col.children.map((child) => pruneSchemaSeedItems(child as object) as object)
        : col.children,
    }));
  }
  if (Array.isArray(rec.tabs)) {
    rec.tabs = (rec.tabs as { label?: unknown; children?: unknown[] }[]).map(
      (tab) => ({
        ...tab,
        // Strip Bio / Press / New Tab seeds so they never publish as real titles.
        label: isSchemaTabLabelPlaceholder(tab.label) ? "" : tab.label,
        children: Array.isArray(tab.children)
          ? tab.children.map(
              (child) => pruneSchemaSeedItems(child as object) as object,
            )
          : tab.children,
      }),
    );
  }

  return rec;
}

const SCHEMA_SEED_KINDS = new Set([
  "cta",
  "ribbon",
  "accordion",
  "discount",
  "testimonials",
  "followers",
  "asseenon",
  "map",
  "sectionheader",
  "columns",
  "video",
]);

function itemHasRealField(
  item: Record<string, unknown> | null | undefined,
  field: string,
  demo: unknown,
): boolean {
  return !isSchemaDemoText(item?.[field], demo);
}

/** Button block link that actually resolves — a bare scheme does not. */
export function schemaCtaHasLink(url: unknown): boolean {
  const href = String(url ?? "").trim();
  return Boolean(href) && href !== "https://" && href !== "http://";
}

/**
 * Discount Codes expiry — strict MM/DD/YYYY (or M/D/YY).
 * Rejects rolled-over Date values like `20/20/26` → Aug 2027.
 */
export function parseDiscountExpiryDate(raw: unknown): Date | null {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  if (!m) return null;
  const month = Number(m[1]);
  const day = Number(m[2]);
  let year = Number(m[3]);
  if (!Number.isFinite(month) || !Number.isFinite(day) || !Number.isFinite(year)) {
    return null;
  }
  if (year < 100) year += 2000;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (year < 2000 || year > 2100) return null;
  const dt = new Date(year, month - 1, day);
  if (
    dt.getFullYear() !== year ||
    dt.getMonth() !== month - 1 ||
    dt.getDate() !== day
  ) {
    return null;
  }
  return dt;
}

export function isValidDiscountExpiryDate(raw: unknown): boolean {
  return parseDiscountExpiryDate(raw) !== null;
}

/**
 * Every ribbon message must be complete before Add / Update.
 * Link label is optional. A filled label on that same message requires a real
 * URL. Any URL that is filled must pass a host check, not just be non-empty.
 */
function ribbonItemsError(items: unknown): string {
  const list = Array.isArray(items) ? items : [];
  const demos = SCHEMA_DEMO.ribbon.items;
  if (!list.length) {
    return "Add a ribbon message before adding to the page.";
  }
  for (let i = 0; i < list.length; i++) {
    const it = (list[i] || {}) as Record<string, unknown>;
    const demo = demos[i] || demos[0];
    const which = list.length > 1 ? ` for message ${i + 1}` : "";
    if (!itemHasRealField(it, "message", demo?.message)) {
      return `Add a message${which} before adding to the page.`;
    }
    const label = String(it.ctaLabel ?? "").trim();
    const url = String(it.url ?? "").trim();
    if (label && !url) {
      return `Add a link URL${which} before adding to the page.`;
    }
    if (url && !validateAndFormatUrl(url).isValid) {
      return `Enter a valid link URL${which} (e.g. example.com).`;
    }
  }
  return "";
}

/**
 * Ribbon / FAQ / Creator Tools / Layout seed copy still unfilled.
 * Walks nested columns & tabs children. Returns "" when complete.
 */
export function schemaSeedPlaceholderError(
  cfg: object | null | undefined,
): string {
  if (!cfg || typeof cfg !== "object") return "";
  const rec = cfg as {
    blockType?: unknown;
    catalogType?: unknown;
    heading?: unknown;
    eyebrow?: unknown;
    sub?: unknown;
    address?: unknown;
    label?: unknown;
    url?: unknown;
    caption?: unknown;
    items?: unknown;
    showHeader?: unknown;
    columns?: unknown;
    tabs?: unknown;
  };
  const kind = String(rec.blockType || rec.catalogType || "");

  if (kind === "ribbon") {
    return ribbonItemsError(rec.items);
  }

  if (kind === "accordion") {
    const items = Array.isArray(rec.items) ? rec.items : [];
    const demos = SCHEMA_DEMO.accordion.items;
    let complete = 0;
    for (let i = 0; i < items.length; i++) {
      const it = (items[i] || {}) as Record<string, unknown>;
      const demo = demos[i] || demos[0];
      const hasQ = itemHasRealField(it, "q", demo.q);
      const hasA = itemHasRealField(it, "a", demo.a);
      // Empty seed rows are pruned on save. A real question still needs an answer.
      if (hasQ && !hasA) return ACCORDION_ANSWER_REQUIRED;
      if (hasQ && hasA) complete += 1;
    }
    if (!complete) return ACCORDION_QUESTION_REQUIRED;
    return "";
  }

  if (kind === "discount") {
    const items = Array.isArray(rec.items) ? rec.items : [];
    const demos = SCHEMA_DEMO.discount.items;
    let complete = 0;
    for (let i = 0; i < items.length; i++) {
      const it = (items[i] || {}) as Record<string, unknown>;
      const demo = demos[i] || demos[0];
      const hasBrand = itemHasRealField(it, "brand", demo.brand);
      const hasCode = itemHasRealField(it, "code", demo.code);
      const hasDesc = itemHasRealField(it, "desc", demo.desc);
      const expiresRaw = String(it.expires || "").trim();
      const which = items.length > 1 ? ` for code ${i + 1}` : "";
      // Empty seed rows are pruned on save.
      if (!hasBrand && !hasCode && !hasDesc && !expiresRaw) continue;
      if (!hasBrand || !hasCode || !hasDesc || !expiresRaw) {
        return `Add a brand, code, description, and expiry${which} before adding to the page.`;
      }
      if (!isValidDiscountExpiryDate(expiresRaw)) {
        return `Enter a valid expiry date${which} (MM/DD/YYYY).`;
      }
      complete += 1;
    }
    if (!complete) {
      return "Add a brand, code, description, and expiry before adding to the page.";
    }
    return "";
  }

  if (kind === "testimonials") {
    if (!String(rec.heading || "").trim()) {
      return "Add a section title before adding to the page.";
    }
    const items = Array.isArray(rec.items) ? rec.items : [];
    const demos = SCHEMA_DEMO.testimonials.items;
    const hasReal = items.some((raw, i) => {
      const it = (raw || {}) as Record<string, unknown>;
      const demo = demos[i] || demos[0];
      return (
        itemHasRealField(it, "quote", demo.quote) &&
        itemHasRealField(it, "name", demo.name)
      );
    });
    if (!hasReal) {
      return "Add a testimonial before adding to the page.";
    }
    return "";
  }

  if (kind === "followers") {
    const items = Array.isArray(rec.items) ? rec.items : [];
    const demos = SCHEMA_DEMO.followers.items;
    const hasReal = items.some((raw, i) => {
      const it = (raw || {}) as Record<string, unknown>;
      const demo = demos[i] || demos[0];
      return (
        itemHasRealField(it, "platform", demo.platform) &&
        itemHasRealField(it, "count", demo.count)
      );
    });
    if (!hasReal) {
      return "Add a follower count before adding to the page.";
    }
    return "";
  }

  if (kind === "asseenon") {
    const items = Array.isArray(rec.items) ? rec.items : [];
    const demos = SCHEMA_DEMO.asseenon.items;
    let complete = 0;
    for (let i = 0; i < items.length; i++) {
      const it = (items[i] || {}) as Record<string, unknown>;
      const demo = demos[i] || demos[0];
      const hasName = itemHasRealField(it, "name", demo.name);
      const hasPhoto = Boolean(String(it.photo || "").trim());
      const which = items.length > 1 ? ` for logo ${i + 1}` : "";
      // Empty / link-only seed rows are pruned on save — don't flag them.
      // A logo photo still needs a publication name (*).
      if (!hasName && !hasPhoto) continue;
      if (hasPhoto && !hasName) {
        return `Add a publication name${which} before adding to the page.`;
      }
      complete += 1;
    }
    if (!complete) {
      return "Add a publication name before adding to the page.";
    }
    return "";
  }

  if (kind === "map") {
    if (isSchemaDemoText(rec.address, SCHEMA_DEMO.map.address)) {
      return "Add a map address before adding to the page.";
    }
    return "";
  }

  if (kind === "sectionheader") {
    if (!String(rec.heading || "").trim()) {
      return "Add a section headline before adding to the page.";
    }
    return "";
  }

  if (kind === "columns") {
    if (
      rec.showHeader === true &&
      !String(rec.heading || "").trim()
    ) {
      return "Add a section headline before adding to the page.";
    }
    // Nested children validated below.
  }

  if (kind === "video") {
    if (!String(rec.url || "").trim()) {
      return "Add a video URL before adding to the page.";
    }
    return "";
  }

  if (kind === "cta") {
    if (!String(rec.label || rec.heading || "").trim()) {
      return "Add a button label before adding to the page.";
    }
    if (!schemaCtaHasLink(rec.url || (rec as { ctaLink?: unknown }).ctaLink)) {
      return "Add a button link before adding to the page.";
    }
    return "";
  }

  if (!SCHEMA_SEED_KINDS.has(kind) && kind !== "tabs" && kind !== "columns") {
    return "";
  }

  const walk = (children: unknown): string => {
    if (!Array.isArray(children)) return "";
    for (const child of children) {
      const err = schemaSeedPlaceholderError(child as object);
      if (err) return err;
    }
    return "";
  };
  if (Array.isArray(rec.columns)) {
    for (const col of rec.columns) {
      const err = walk((col as { children?: unknown })?.children);
      if (err) return err;
    }
  }
  if (Array.isArray(rec.tabs)) {
    for (const tab of rec.tabs) {
      const err = walk((tab as { children?: unknown })?.children);
      if (err) return err;
    }
  }
  return "";
}

export const TEXT_MEDIA_CATALOG_TYPES = new Set([
  "text",
  "image",
  "quote",
  "embed",
  "spacer",
]);

export function isTextMediaCatalogType(catalogType: string): boolean {
  return TEXT_MEDIA_CATALOG_TYPES.has(catalogType);
}

export function isSchemaTextBodyPlaceholder(body: unknown): boolean {
  const raw = String(body ?? "").trim();
  if (!raw) return true;
  const plain = stripHtmlForCompare(raw);
  if (!plain) return true;
  return plain === SCHEMA_TEXT_DEFAULT_BODY_PLAIN;
}

/** True when rich HTML has visible text (ignores `<br>`, empty tags, nbsp). */
export function richHtmlHasText(html: unknown): boolean {
  return Boolean(stripHtmlForCompare(String(html ?? "")));
}

function stripHtmlForCompare(html: string): string {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

const SCHEMA_TEXT_DEFAULT_BODY_PLAIN = stripHtmlForCompare(
  SCHEMA_TEXT_DEFAULT_BODY,
);

/** Plain-text hint for drawer/canvas placeholders (no HTML). */
export function schemaTextDefaultBodyPlain(): string {
  return SCHEMA_TEXT_DEFAULT_BODY.replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

type SchemaCfgLike = {
  blockType?: unknown;
  catalogType?: unknown;
  body?: unknown;
  rich?: unknown;
  quote?: unknown;
  embedCode?: unknown;
  url?: unknown;
  columns?: unknown;
  tabs?: unknown;
};

export const TABS_TITLE_REQUIRED = "Each tab needs a title.";
export const TABS_COMPONENT_REQUIRED = "Add a component to each tab.";

/**
 * Text & Media blocks (text, image, quote, embed) still on factory defaults.
 * Spacer/divider has no required copy. Walks nested columns/tabs children.
 */
export function schemaTextMediaPlaceholderError(
  cfg: object | null | undefined,
): string {
  if (!cfg || typeof cfg !== "object") return "";
  const rec = cfg as SchemaCfgLike;
  const kind = String(rec.blockType || rec.catalogType || "");

  if (kind === "text") {
    const body = String(rec.body || rec.rich || "");
    if (isSchemaTextBodyPlaceholder(body)) {
      return "Replace the default Text content before publishing.";
    }
    return "";
  }
  if (kind === "quote") {
    if (!String(rec.quote || "").trim()) {
      return "Add quote text before publishing.";
    }
    return "";
  }
  if (kind === "embed") {
    if (!String(rec.embedCode || rec.url || "").trim()) {
      return "Add embed code before publishing.";
    }
    return "";
  }
  if (kind === "image") {
    const state = imageCfgNeedsUpload(rec);
    if (state === "missing") {
      return "Upload at least one image before publishing.";
    }
    if (state === "uploading") {
      return "Wait for image uploads to finish before publishing.";
    }
    if (imageGalleryMarqueeSecondImageMissing(rec)) {
      return `${IMAGE_GALLERY_MARQUEE_SECOND_IMAGE_REQUIRED} before publishing.`;
    }
    return "";
  }
  if (kind === "spacer") return "";

  const walk = (children: unknown): string => {
    if (!Array.isArray(children)) return "";
    for (const child of children) {
      const err = schemaTextMediaPlaceholderError(child as object);
      if (err) return err;
    }
    return "";
  };
  if (Array.isArray(rec.columns)) {
    for (const col of rec.columns) {
      const err = walk((col as { children?: unknown })?.children);
      if (err) return err;
    }
  }
  if (Array.isArray(rec.tabs)) {
    for (const tab of rec.tabs) {
      const err = walk((tab as { children?: unknown })?.children);
      if (err) return err;
    }
  }
  return "";
}

/** Nested block still missing its own required fields. */
export function nestedComponentPlaceholderError(
  child: object | null | undefined,
): string {
  return nestedSchemaChildError(child, "Add this component's content.", "section");
}

function nestedSchemaChildError(
  child: object | null | undefined,
  emptyMessage: string,
  _where: "tab" | "section",
): string {
  if (!child || typeof child !== "object") return emptyMessage;
  const kind = String(
    (child as SchemaCfgLike).blockType ||
      (child as SchemaCfgLike).catalogType ||
      "",
  );
  if (!kind || kind === "spacer") return "";
  const media = schemaTextMediaPlaceholderError(child);
  if (media) return media;
  const seed = schemaSeedPlaceholderError(child);
  if (seed) return seed;
  return "";
}

/** Incomplete child inside Tabs or Content Section. Empty slots are allowed. */
export function schemaNestedContentError(
  cfg: object | null | undefined,
): string {
  if (!cfg || typeof cfg !== "object") return "";
  const rec = cfg as SchemaCfgLike;
  const kind = String(rec.blockType || rec.catalogType || "");
  const groups =
    kind === "tabs" ? rec.tabs : kind === "columns" ? rec.columns : null;
  if (!Array.isArray(groups)) return "";
  for (const group of groups) {
    const children = (group as { children?: unknown } | null)?.children;
    if (!Array.isArray(children)) continue;
    for (const nested of children) {
      const err = nestedComponentPlaceholderError(nested as object);
      if (err) return err;
    }
  }
  return "";
}

/**
 * Tabs add drawer. Every tab needs a title and a complete nested component.
 */
export function tabsDrawerError(cfg: object | null | undefined): string {
  return tabsValidationError(cfg);
}

/** Tabs block only — other catalog types always return "". */
export function tabsValidationError(
  cfg: object | null | undefined,
): string {
  if (!cfg || typeof cfg !== "object") return "";
  const rec = cfg as SchemaCfgLike;
  if (String(rec.blockType || rec.catalogType || "") !== "tabs") return "";
  const tabs = Array.isArray(rec.tabs) ? rec.tabs : [];
  if (tabs.length === 0) return TABS_TITLE_REQUIRED;
  for (const tab of tabs) {
    if (
      isSchemaTabLabelPlaceholder(
        (tab as { label?: unknown } | null)?.label,
      )
    ) {
      return TABS_TITLE_REQUIRED;
    }
  }
  for (const tab of tabs) {
    const children = (tab as { children?: unknown } | null)?.children;
    if (!Array.isArray(children) || children.length === 0) {
      return TABS_COMPONENT_REQUIRED;
    }
    for (const nested of children) {
      const err = nestedSchemaChildError(
        nested as object,
        TABS_COMPONENT_REQUIRED,
        "tab",
      );
      if (err) return err;
    }
  }
  return "";
}

export const COLUMNS_HEADLINE_REQUIRED = "Add a section headline.";
export const COLUMNS_COMPONENT_REQUIRED = "Add a component to each column.";

/** Content Section only — other catalog types always return "". */
export function columnsValidationError(
  cfg: object | null | undefined,
): string {
  if (!cfg || typeof cfg !== "object") return "";
  const rec = cfg as SchemaCfgLike & {
    showHeader?: unknown;
    heading?: unknown;
  };
  if (String(rec.blockType || rec.catalogType || "") !== "columns") return "";
  if (
    rec.showHeader === true &&
    isSchemaDemoText(rec.heading, SCHEMA_DEMO.columns.heading)
  ) {
    return COLUMNS_HEADLINE_REQUIRED;
  }
  const columns = Array.isArray(rec.columns) ? rec.columns : [];
  if (columns.length === 0) return COLUMNS_COMPONENT_REQUIRED;
  for (const col of columns) {
    const children = (col as { children?: unknown } | null)?.children;
    if (!Array.isArray(children) || children.length === 0) {
      return COLUMNS_COMPONENT_REQUIRED;
    }
    for (const nested of children) {
      const err = nestedSchemaChildError(
        nested as object,
        COLUMNS_COMPONENT_REQUIRED,
        "section",
      );
      if (err) return err;
    }
  }
  return "";
}

type AccordionCfgLike = {
  blockType?: unknown;
  catalogType?: unknown;
  items?: unknown;
};

/** Accordion / FAQ only — other catalog types are never incomplete FAQs. */
export function accordionMissingQuestion(
  cfg: object | null | undefined,
): boolean {
  if (!cfg || typeof cfg !== "object") return false;
  const rec = cfg as AccordionCfgLike;
  const kind = String(rec.blockType || rec.catalogType || "");
  if (kind !== "accordion") return false;
  return Boolean(schemaSeedPlaceholderError({ ...rec, blockType: "accordion" }));
}

export function websiteHasIncompleteAccordion(
  tweaks:
    | {
        pages?: Record<
          string,
          { cfg?: Record<string, AccordionCfgLike | undefined> }
        >;
      }
    | null
    | undefined,
): boolean {
  const pages = tweaks?.pages || {};
  for (const page of Object.values(pages)) {
    const cfg = page?.cfg || {};
    for (const section of Object.values(cfg)) {
      if (accordionMissingQuestion(section)) return true;
    }
  }
  return false;
}

export function extraSectionTypeForCatalog(
  catalogType: string,
  pageKey?: string,
): string | null {
  if (pageKey === "shop") {
    if (catalogType === "store-products") return "StoreProduct";
    if (catalogType === "featured-category") return "FeaturedCategory";
    if (catalogType === "featured-categories") return "FeaturedCategories";
    if (catalogType === "featured-collections") return "FeaturedCollections";
    if (catalogType === "featured-brands") return "FeaturedBrands";
    if (catalogType === "shop-brand") return "ShopBrandCollection";
    if (catalogType === "trending-products") return "ShopTrendingCollection";
  }
  if (pageKey === "content") {
    if (catalogType === "featured-outfit") return "FeaturedOutfit";
    if (catalogType === "recent-looks") return "RecentLooks";
    if (catalogType === "featured-categories") return "FeaturedCategories";
    if (catalogType === "featured-collections") return "FeaturedCollections";
  }
  const schema = schemaBlockTypeForCatalog(catalogType);
  if (schema) return schema;
  if (
    catalogType === "content-feed" ||
    catalogType === "trending-content" ||
    catalogType === "content-keyword" ||
    catalogType === "custom-content" ||
    catalogType === "featured-content" ||
    catalogType === "product-feed" ||
    catalogType === "trending-products" ||
    catalogType === "product-keyword" ||
    catalogType === "custom-products" ||
    catalogType === "group"
  ) {
    return "Carousel";
  }
  if (catalogType === "banner") return "Banner";
  if (catalogType === "highlight") return "HighlightCards";
  if (catalogType === "link-section") return "Links";
  if (catalogType === "newsletter" || catalogType === "campaign") {
    return "Newsletter";
  }
  if (catalogType === "page-header") return "PageHeader";
  return null;
}

export function extraSectionLabelForCatalog(
  catalogType: string,
  _title?: string,
): string {
  // Chrome / inspector always use the catalog component name — never the
  // content title (button label, heading, feed title, etc.).
  const names: Record<string, string> = {
    image: "Image / Gallery",
    text: "Text",
    quote: "Quote",
    spacer: "Spacer / Divider",
    cta: "Button",
    ribbon: "Ribbon Alert",
    video: "Video Player",
    discount: "Discount Codes",
    testimonials: "Testimonials",
    followers: "Follower Counts",
    asseenon: "As Seen On",
    map: "Map",
    accordion: "Accordion / FAQ",
    "page-header": "Header",
    sectionheader: "Section Header",
    vibe: "Custom Section",
    embed: "Embed",
    columns: "Content Section",
    tabs: "Tabs",
    newsletter: "Newsletter Signup",
    banner: "Banner",
    highlight: "Highlight",
    "link-section": "Link Section",
    "content-feed": "Recent Content",
    "trending-content": "Trending Content",
    "content-keyword": "Word or Phrase",
    "custom-content": "Custom Content",
    "featured-content": "Featured",
    "product-feed": "Recent Products",
    "trending-products": "Trending Products",
    "product-keyword": "Word or Phrase",
    "custom-products": "Custom Products",
    "store-products": "Digital Product",
    "featured-category": "Featured Category",
    "featured-categories": "Featured Categories",
    "featured-collections": "Featured Collections",
    "featured-brands": "Featured Brands",
    "shop-brand": "Shop a Brand",
    "featured-outfit": "Featured Outfit",
    "recent-looks": "Recent Looks",
    group: "Group Components",
    campaign: "Campaign",
  };
  return names[catalogType] || "Component";
}

/** Hover chrome badge: catalog component name, never the section heading/title. */
export function websiteSectionHoverLabel(args: {
  sectionType: string;
  catalogType?: string | null;
  blockType?: string | null;
  fallback?: string;
}): string {
  const catalog = String(
    args.catalogType || args.blockType || "",
  ).trim();
  if (catalog) return extraSectionLabelForCatalog(catalog);
  if (args.sectionType === "Bio") return "Hero";
  if (args.sectionType === "Newsletter") return "Newsletter Signup";
  if (args.sectionType === "StoreProduct") return "Digital Product";
  if (args.sectionType === "Banner") return "Banner";
  if (args.sectionType === "HighlightCards") return "Highlight";
  return String(args.fallback || args.sectionType || "Component").trim() || "Component";
}

export function nextExtraSectionId(catalogType: string): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `x_${catalogType.replace(/[^a-z0-9]+/gi, "")}_${rand}`;
}

export function uniqueCustomPageSlug(
  desired: string,
  existing: CustomWebsitePage[],
): string {
  let slug = slugifyPageName(desired) || "page";
  if (RESERVED_PAGE_SLUGS.has(slug)) slug = `${slug}-page`;
  const taken = new Set(existing.map((p) => p.slug));
  if (!taken.has(slug)) return slug;
  let i = 2;
  while (taken.has(`${slug}-${i}`)) i += 1;
  return `${slug}-${i}`;
}

/**
 * Hard conflict check for create/edit — never auto-renames.
 * Only live `customPages` count (deleted pages are already removed from the list).
 */
export function getCustomPageSlugConflict(
  desired: string,
  existing: CustomWebsitePage[],
  opts?: { excludeId?: string },
): string | null {
  const slug = slugifyPageName(desired);
  if (!slug) return "Enter a page link.";
  if (RESERVED_PAGE_SLUGS.has(slug)) {
    return `“/${slug}” is reserved. Choose a different link.`;
  }
  const exclude = String(opts?.excludeId || "").trim();
  const conflict = existing.find((p) => {
    if (exclude && p.id === exclude) return false;
    const other = String(p.slug || "").trim().toLowerCase();
    const otherIdSlug = p.id.startsWith(CUSTOM_PAGE_PREFIX)
      ? p.id.slice(CUSTOM_PAGE_PREFIX.length).toLowerCase()
      : "";
    return other === slug || otherIdSlug === slug || p.id === customPageIdFromSlug(slug);
  });
  if (!conflict) return null;
  const label = String(conflict.label || conflict.slug || "another page").trim();
  return `“/${slug}” is already used by ${label}. Choose a different link.`;
}

/**
 * Drop a creator-made page from tweaks (list, page cfg, nav order/omit, label/href edits).
 * Built-in pages are not removable — only `c_*` keys.
 */
export function removeCustomWebsitePageFromTweaks(
  tweaks: Tweaks,
  pageId: string,
): Tweaks | null {
  if (!isCustomWebsitePageKey(pageId)) return null;
  const existing = listCustomWebsitePages(tweaks);
  if (!existing.some((p) => p.id === pageId)) return null;

  const nextCustom = existing.filter((p) => p.id !== pageId);
  const pages = { ...(tweaks.pages || {}) };
  delete pages[pageId];

  const contentEdits = { ...(tweaks.contentEdits || {}) };
  const dropKeys = [
    `nav.${pageId}`,
    `nav.${pageId}.text`,
    `nav.${pageId}.href`,
    `nav.slug.${pageId}`,
  ];
  for (const k of dropKeys) delete contentEdits[k];
  for (const k of Object.keys(contentEdits)) {
    if (k.startsWith(`section.${pageId}/`) || k.startsWith(`section.${pageId}.`)) {
      delete contentEdits[k];
    }
  }

  const orderRaw = contentEdits["nav.order"];
  if (typeof orderRaw === "string" && orderRaw.trim()) {
    const nextOrder = orderRaw
      .split(",")
      .map((s) => s.trim())
      .filter((k) => k && k !== pageId);
    contentEdits["nav.order"] = nextOrder.join(",");
  }
  const omitRaw = contentEdits["nav.omit"];
  if (typeof omitRaw === "string" && omitRaw.trim()) {
    const nextOmit = omitRaw
      .split(",")
      .map((s) => s.trim())
      .filter((k) => k && k !== pageId);
    contentEdits["nav.omit"] = nextOmit.join(",");
  }

  return {
    ...tweaks,
    customPages: nextCustom,
    pages,
    contentEdits,
  };
}

function uid(): string {
  return `id_${Math.random().toString(36).slice(2, 9)}`;
}

const SCHEMA_NO_CANVAS_HEADING = new Set([
  "image",
  "text",
  "quote",
  "embed",
  "spacer",
  "cta",
  "ribbon",
  "video",
  "map",
  "vibe",
  "tabs",
  "discount",
  "followers",
  // FAQ heading is optional canvas copy, not a unique stay-db title.
  "accordion",
  // Headline / section title live in SchemaBlockInspector — not a separate
  // Add "Title" field. Seeding draftTitle (e.g. "Section") overwrote preview
  // and persisted the wrong heading on Add.
  "sectionheader",
  "testimonials",
  "asseenon",
  "columns",
]);

export function schemaInsertSkipsHeading(catalogType: string): boolean {
  return SCHEMA_NO_CANVAS_HEADING.has(catalogType);
}

export function defaultSchemaCfg(
  catalogType: string,
  title?: string,
): import("./types").SectionCfg {
  const heading = String(title || extraSectionLabelForCatalog(catalogType)).trim();
  // Title rule: seed canvas heading with the drawer type unless the block
  // uses a different primary field (Button label, ribbon messages, etc.).
  const omitHeadingSeed =
    catalogType === "cta" ||
    catalogType === "ribbon" ||
    catalogType === "spacer" ||
    catalogType === "sectionheader" ||
    catalogType === "columns" ||
    catalogType === "embed" ||
    catalogType === "vibe";
  const base: import("./types").SectionCfg = {
    blockType: catalogType,
    catalogType,
    bgTone: "bg",
    ...(!omitHeadingSeed && heading ? { heading } : {}),
  };
  if (catalogType === "image") {
    return {
      ...base,
      layout: "single",
      // Text & Media Drawers.html — Grid columns default to 2.
      cols: "2",
      motion: "static",
      autoscroll: false,
      // Text & Media Drawers.html — Show captions on by default.
      captions: true,
      items: [
        { id: uid(), photo: "", caption: "" },
        { id: uid(), photo: "", caption: "" },
      ],
    };
  }
  if (catalogType === "text") {
    return {
      ...base,
      layout: "left",
      showTitle: true,
      body: "",
    };
  }
  if (catalogType === "quote") {
    return {
      ...base,
      layout: "left",
      quote: "",
      author: "",
      // Text & Media Drawers.html — Show quotation mark starts off.
      marks: false,
    };
  }
  if (catalogType === "spacer") {
    return { ...base, layout: "space", thick: false };
  }
  if (catalogType === "cta") {
    return {
      blockType: catalogType,
      catalogType,
      bgTone: "bg",
      layout: "filled",
      // Title rule — Button label prefilled with drawer type ("Button").
      label: heading || "Button",
      url: "",
      newtab: true,
      wide: false,
      size: "m",
    };
  }
  if (catalogType === "ribbon") {
    return {
      blockType: catalogType,
      catalogType,
      bgTone: "bg",
      // Links & Features Drawers.html — Scrolling default.
      layout: "marquee",
      dismissible: true,
      icon: true,
      items: [
        {
          id: uid(),
          message: "",
          ctaLabel: "",
          url: "",
        },
        {
          id: uid(),
          message: "",
          ctaLabel: "",
          url: "",
        },
      ],
    };
  }
  if (catalogType === "video") {
    return {
      ...base,
      // Text & Media Drawers.html — video link + background only (no layout).
      url: "",
      caption: "",
      poster: "",
      autoplay: false,
      controls: true,
    };
  }
  if (catalogType === "discount") {
    return {
      ...base,
      // Creator Tools Drawers.html — Carousel default; title "Discount Codes".
      layout: "carousel",
      heading: heading || "Discount Codes",
      copy: true,
      countdown: true,
      brand: true,
      items: [
        {
          id: uid(),
          brand: "",
          code: "",
          desc: "",
          expires: "",
          url: "",
        },
        {
          id: uid(),
          brand: "",
          code: "",
          desc: "",
          expires: "",
          url: "",
        },
      ],
    };
  }
  if (catalogType === "testimonials") {
    return {
      ...base,
      // Creator Tools Drawers.html — Carousel + Static Motion; title Testimonials.
      heading: heading || "Testimonials",
      layout: "carousel",
      motion: "static",
      autoplay: false,
      items: [
        {
          id: uid(),
          quote: "",
          name: "",
        },
        {
          id: uid(),
          quote: "",
          name: "",
        },
      ],
    };
  }
  if (catalogType === "followers") {
    return {
      ...base,
      layout: "inline",
      heading: heading || "Audience across platforms",
      icons: true,
      labels: true,
      items: [
        { id: uid(), platform: "", count: "", url: "" },
        { id: uid(), platform: "", count: "", url: "" },
      ],
    };
  }
  if (catalogType === "asseenon") {
    return {
      ...base,
      heading: heading || "As Seen On",
      layout: "marquee",
      // Creator Tools Drawers.html — Auto-scroll off by default.
      autoplay: false,
      showTitle: true,
      items: [
        { id: uid(), name: "", url: "", photo: "" },
        { id: uid(), name: "", url: "", photo: "" },
      ],
    };
  }
  if (catalogType === "map") {
    return {
      ...base,
      layout: "inset",
      address: "",
      label: "",
      pinlabel: true,
      addresstext: true,
      directions: true,
    };
  }
  if (catalogType === "accordion") {
    return {
      ...base,
      // Creator Tools Drawers.html — Section title defaults to FAQ.
      heading: heading || "FAQ",
      expand: false,
      items: [
        {
          id: uid(),
          q: "",
          a: "",
        },
        {
          id: uid(),
          q: "",
          a: "",
        },
      ],
    };
  }
  if (catalogType === "sectionheader") {
    return {
      ...base,
      layout: "left",
      eyebrow: "",
      heading: "",
      sub: "",
      divider: true,
    };
  }
  if (catalogType === "vibe") {
    return { ...base, layout: "inset", vibeOutput: "" };
  }
  if (catalogType === "embed") {
    return { ...base, layout: "inset", embedCode: "" };
  }
  if (catalogType === "columns") {
    return {
      ...base,
      heading: "",
      layout: "col2",
      eyebrow: "",
      sub: "",
      headerAlign: "left",
      showHeader: false,
      dividers: true,
      columns: [
        { id: uid(), children: [] },
        { id: uid(), children: [] },
      ],
    };
  }
  if (catalogType === "tabs") {
    return {
      ...base,
      activeTab: 0,
      tabs: [
        { id: uid(), label: "", children: [] },
        { id: uid(), label: "", children: [] },
      ],
    };
  }
  return base;
}
